import React, { useContext, useEffect, useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { Button } from '@material-ui/core';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { Grid, Typography } from '@mui/material';
import LocalizationContext from '../../../../localization/LocalizationContext';
import useReactHookFormStyles from '../../../../custom-components/form-components/ReactHookForm.styles';
import { fetchOptions } from '../queries';
import TextFieldInput from './TextField';
import SelectInput from './Select';

const FieldMap = {
    TextField: TextFieldInput,
    Select: SelectInput,
};

function FieldArray({ name, ArrayFields, optionsCheck, props: { label, reducePadding = false, ...rest } }) {
    const [hasOptions, setHasOptions] = useState(null);
    const localization = useContext(LocalizationContext);
    const {
        control,
        getValues,
        updateSelectedValues,
        watch,
        // wrap
    } = useFormContext();
    const { fieldArray } = useReactHookFormStyles();

    const { fields, append, remove } = useFieldArray({
        control,
        name,
    });

    const { referenceField, referenceKey, endpoint, useLoopback, alwaysShowAdd, emptyMessage, where: checkWhere } = optionsCheck || {};

    useEffect(() => {
        const value = getValues(referenceField);

        // TODO: Add support for checking options on multiple fields
        // TODO: Consider piping checked options into field select component
        const checkForOptions = async () => {
            const optionsResponse = await fetchOptions({ endpoint, where: { ...checkWhere, [referenceKey]: value }, useLoopback });
            if (optionsResponse.length) {
                setHasOptions(true);
            } else {
                setHasOptions(false);
            }
        };

        if (value && optionsCheck) {
            checkForOptions();
        }
    }, [referenceField && watch(referenceField)]);

    useEffect(() => {
        const values = getValues(name);
        updateSelectedValues(name, values);
    }, [watch(name)]);

    const appendField = () => {
        const newField = {};
        ArrayFields.forEach((ArrayField) => {
            newField[ArrayField.name] = null;
        });
        append(newField);
    };

    const showAddButton = !optionsCheck || hasOptions || alwaysShowAdd;
    const showEmptyMessage = hasOptions === false && !!emptyMessage;
    const message = showEmptyMessage ? emptyMessage.replace('{processor}', localization?.general?.processor_singular ?? 'Plant') : null;

    if (!showAddButton && !showEmptyMessage && fields.length === 0) return null;

    return (
        <>
            {fields.map((field, index) => (
                <Grid key={`${field.name}.${field.id}`} container className={fieldArray} spacing={reducePadding ? 1 : 2} style={{ alignItems: 'flex-start' }}>
                    {ArrayFields.map((ArrayField) => {
                        const Component = FieldMap[ArrayField.Component];
                        return (
                            <Grid item {...ArrayField.widths}>
                                <Component
                                    {...ArrayField}
                                    key={`${ArrayField.name}.${field.id}`}
                                    name={`${name}.${index}.${ArrayField.name}`}
                                    fieldName={name}
                                    componentName={ArrayField.name}
                                    setHasOptions={setHasOptions}
                                    {...{ control, index, field }}
                                    // wrap
                                />
                            </Grid>
                        );
                    })}
                    <Grid item xs={reducePadding ? 0.75 : 2} style={{ paddingTop: reducePadding ? 8 : 16 }}>
                        <Button onClick={() => remove(index)}>
                            <HighlightOffIcon />
                        </Button>
                    </Grid>
                </Grid>
            ))}
            {showAddButton && (
                <Button variant="contained" onClick={appendField}>
                    Add {label}
                </Button>
            )}
            {showEmptyMessage && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {message}
                </Typography>
            )}
        </>
    );
}

export default FieldArray;
